// src/pages/Dashboard/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  useTheme,
  LinearProgress,
  Divider,
  Paper,
  Stack,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  CreditCard,
  AccountBalance,
  Receipt,
  Analytics,
  CheckCircle,
  Schedule,
  Add,
  ArrowForward,
  PersonAdd,
  Support,
  Euro,
  AttachMoney,
  Business,
  Timeline,
  Notifications,
  Security,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import DashboardService from '../../services/dashboardService';
import LimitService from '../../services/limitService';
import useRoleColors from '../../hooks/useRoleColors';

// Icon mapping for dynamic icons
const getIconComponent = (iconName) => {
  const iconMap = {
    'PersonAdd': <PersonAdd />,
    'CreditCard': <CreditCard />,
    'Schedule': <Schedule />,
    'Analytics': <Analytics />,
    'AccountBalance': <AccountBalance />,
    'CheckCircle': <CheckCircle />,
    'Add': <Add />,
    'Receipt': <Receipt />,
  };
  return iconMap[iconName] || <CreditCard />;
};

// Shorten card titles to fit in one line
const getShortTitle = (title) => {
  const titleMap = {
    'Payments': 'Payments',
    'Pending Approvals': 'Pending Approvals',
    'Discount Loans': 'Discount Loans',
    'Available Limit': 'Available Limit',
    'Pending Discounts': 'Pending Discounts',
    'Pending Reviews': 'Pending Discounts', // Legacy support
  };
  return titleMap[title] || title;
};

const StatCard = ({ stat, onClick }) => {
  const theme = useTheme();
  const isAvailableLimit = stat.title === 'Available Limit' && stat.totalLimit != null && stat.usedLimit != null;
  const progressPercentage = isAvailableLimit && stat.totalLimit > 0 
    ? (stat.usedLimit / stat.totalLimit) * 100 
    : 0;
  
  // Determine progress bar color based on usage
  const getProgressColor = () => {
    if (progressPercentage >= 90) return 'error';
    if (progressPercentage >= 70) return 'warning';
    return 'success';
  };
  
  const iconColor = theme.palette[stat.color]?.main || theme.palette.primary.main;
  const iconBackground = alpha(iconColor, theme.palette.mode === 'dark' ? 0.28 : 0.15);

  return (
    <Card elevation={8} sx={{ 
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 4,
      transition: 'all 0.3s ease',
      cursor: onClick ? 'pointer' : 'default',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      minHeight: '100%',
      '&:hover': {
        transform: 'translateY(-6px)',
        boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
      }
    }}
    onClick={onClick}
    >
      <CardContent sx={{ 
        p: { xs: 2, sm: 2.5 }, 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          mb: 1.5, 
          gap: { xs: 1, sm: 1.5 },
          width: '100%',
          overflow: 'hidden'
        }}>
          <Box sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: '100%',
            minWidth: 0,
            overflow: 'hidden'
          }}>
            <Typography color="text.secondary" gutterBottom variant="body2" sx={{ 
              textTransform: 'uppercase', 
              letterSpacing: 0.5,
              fontWeight: 600,
              fontSize: { xs: '0.6rem', sm: '0.65rem' },
              lineHeight: 1.2,
              mb: 0.5,
              whiteSpace: 'normal',
              wordBreak: 'break-word'
            }}>
              {stat.title}
            </Typography>
            <Typography variant="h5" component="div" sx={{ 
              fontWeight: 700, 
              mb: 0.5, 
              color: 'text.primary', 
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              wordBreak: 'break-word'
            }}>
              {stat.value}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ 
              mb: 1, 
              fontSize: { xs: '0.7rem', sm: '0.75rem' }
            }}>
              {stat.subtitle || 'Last month'}
            </Typography>
            <Box sx={{ mb: 1, minHeight: 28, display: 'flex', alignItems: 'center' }}>
              <Chip 
                label={stat.change || '0%'} 
                color={stat.color}
                size="small"
                icon={stat.trend === 'up' ? <TrendingUp /> : <TrendingDown />}
                sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  height: { xs: 22, sm: 24 },
                  '& .MuiChip-icon': {
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }
                }}
              />
            </Box>
            {isAvailableLimit && (
              <Box sx={{ mt: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ 
                    fontSize: { xs: '0.65rem', sm: '0.7rem' }
                  }}>
                    Total Limit: ₺{stat.totalLimit?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '0'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(progressPercentage, 100)}
                    color={getProgressColor()}
                    sx={{
                      flex: 1,
                      height: { xs: 5, sm: 6 },
                      borderRadius: 3,
                      backgroundColor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 'fit-content' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ 
                      fontWeight: 600, 
                      fontSize: { xs: '0.65rem', sm: '0.7rem' }
                    }}>
                      ({progressPercentage.toFixed(1)}%)
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          <Box sx={{ 
            ml: { xs: 0.75, sm: 1 },
            mr: 0,
            mt: { xs: 1, sm: 1.5 },
            p: { xs: 1.1, sm: 1.3 }, 
            borderRadius: 3.5, 
            backgroundColor: iconBackground,
            border: `1px solid ${alpha(iconColor, 0.2)}`,
            color: iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: { xs: 40, sm: 44 },
            width: { xs: 40, sm: 44 },
            height: { xs: 40, sm: 44 },
            flexShrink: 0,
            alignSelf: 'flex-start',
            boxShadow: `0 6px 16px ${alpha(iconColor, 0.18)}`,
            '& svg': { fontSize: { xs: 20, sm: 22 } }
          }}>
            {getIconComponent(stat.icon)}
          </Box>
        </Box>
        
      </CardContent>
    </Card>
  );
};

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const { accentGradient } = useRoleColors();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requestingLimit, setRequestingLimit] = useState(false);

  // Fetch dashboard data from API
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const result = await DashboardService.getDashboardData();
        if (result.success) {
          setDashboardData(result.data);
          setError('');
        } else {
          setError(result.message);
        }
      } catch (err) {
        setError('An error occurred while loading dashboard data');
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        py: 4,
        px: { xs: 2, sm: 3 },
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center' 
      }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={60} sx={{ color: '#1976d2' }} />
          <Typography variant="body1" color="text.secondary">
            Loading dashboard...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        py: 4,
        px: { xs: 2, sm: 3 }
      }}>
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              }
            }}
          >
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }

  // Show fallback if no data
  if (!dashboardData) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        py: 4,
        px: { xs: 2, sm: 3 }
      }}>
        <Box sx={{ maxWidth: 600, mx: 'auto' }}>
          <Alert severity="warning">
            Dashboard data not found
          </Alert>
        </Box>
      </Box>
    );
  }

  const { stats, recentActivities, quickActions, hasPendingLimitRequest } = dashboardData;

  // Check if user has a limit (Available Limit card's TotalLimit is 0 or null means no limit)
  const hasLimit = stats?.some(stat => 
    stat.title === 'Available Limit' && 
    stat.totalLimit != null && 
    stat.totalLimit > 0
  ) ?? false;

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'success': return 'success.main';
      case 'warning': return 'warning.main';
      case 'error': return 'error.main';
      default: return 'primary.main';
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      py: 4,
      px: { xs: 2, sm: 3 }
    }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        
        {/* Main Card */}
        <Card elevation={24} sx={{ 
          borderRadius: 4,
          overflow: 'hidden'
        }}>
          
          {/* Welcome Banner */}
          <Box sx={{ 
            background: accentGradient,
            p: 3,
            color: 'white'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ 
                  width: 64, 
                  height: 64,
                  background: 'rgba(255,255,255,0.25)',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                  border: '3px solid rgba(255,255,255,0.3)'
                }}>
                  {user.name && user.surname ? 
                    `${user.name.charAt(0)}${user.surname.charAt(0)}`.toUpperCase() : 
                    user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {getGreeting()}, {user.name?.split(' ')[0]}! 👋
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Welcome back to your dashboard
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4 }}>

        {/* Stats Cards */}
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 3 },
            mb: 4,
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(4, minmax(0, 1fr))',
            },
            gridAutoRows: { xs: 'minmax(180px, auto)', md: 'minmax(220px, auto)' },
            alignItems: 'stretch',
          }}
        >
          {stats.map((stat, index) => {
            // Determine navigation path based on stat title and user role
            const getNavigationPath = () => {
              const userRole = dashboardData?.User?.Role || user?.role;
              
              // Admin navigation paths
              if (userRole === 'Admin') {
                if (stat.title === 'Total Users' || stat.title === 'Monthly Volume') {
                  return '/admin/users/all';
                } else if (stat.title === 'Active Limits') {
                  return '/admin/limits/view';
                } else if (stat.title === 'Pending Discounts' || stat.title === 'Pending Reviews') {
                  return '/admin/discounts';
                }
                return undefined;
              }
              
              // User navigation paths
              if (stat.title === 'Available Limit' || stat.title === 'Payments') {
                return '/user/credits';
              } else if (stat.title === 'Pending Approvals') {
                return '/user/approvals';
              } else if (stat.title === 'Discount Loans') {
                return '/user/discount/list';
              }
              return undefined;
            };

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  minHeight: { xs: 180, md: 220 },
                }}
              >
                <StatCard
                  stat={stat}
                  onClick={getNavigationPath() ? () => navigate(getNavigationPath()) : undefined}
                />
              </Box>
            );
          })}
        </Box>

        {/* Quick Actions */}
        <Box sx={{ mb: 4 }}>
          <Card elevation={16} sx={{ 
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            borderTop: '2px solid',
            borderTopColor: 'divider'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Business sx={{ mr: 1.5, color: '#1976d2', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Quick Actions
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gap: { xs: 2, md: 3 },
                  gridTemplateColumns: {
                    xs: 'repeat(1, minmax(0, 1fr))',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(4, minmax(0, 1fr))',
                  },
                  gridAutoRows: '1fr',
                }}
              >
                {quickActions.map((action, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Card 
                      elevation={4}
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        borderRadius: 3,
                        border: '2px solid transparent',
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                          borderColor: `${action.color}.main`,
                          '& .action-icon': {
                            transform: 'scale(1.15)',
                          }
                        }
                      }}
                      onClick={() => navigate(action.path)}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 3, px: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box sx={{ 
                          mb: 2, 
                          color: `${action.color}.main`,
                          '& svg': { fontSize: 48 },
                          transition: 'transform 0.3s ease'
                        }} className="action-icon">
                          {getIconComponent(action.icon)}
                        </Box>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                          {action.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, flexGrow: 1 }}>
                          {action.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
                {/* Limit Request from Bank - Only for User without limit */}
                {(dashboardData?.User?.Role === 'User' || user?.role === 'User') && !hasLimit && (
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Card 
                      elevation={hasPendingLimitRequest ? 2 : 4}
                      sx={{ 
                        cursor: (requestingLimit || hasPendingLimitRequest) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        borderRadius: 3,
                        border: '2px solid transparent',
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        opacity: hasPendingLimitRequest ? 0.7 : 1,
                        backgroundColor: hasPendingLimitRequest ? 'action.disabledBackground' : 'background.paper',
                        '&:hover': {
                          transform: (requestingLimit || hasPendingLimitRequest) ? 'none' : 'translateY(-8px)',
                          boxShadow: (requestingLimit || hasPendingLimitRequest) ? 'none' : '0 12px 24px rgba(0,0,0,0.15)',
                          borderColor: (requestingLimit || hasPendingLimitRequest) ? 'transparent' : 'info.main',
                          '& .action-icon': {
                            transform: (requestingLimit || hasPendingLimitRequest) ? 'none' : 'scale(1.15)',
                          }
                        }
                      }}
                      onClick={async () => {
                        if (requestingLimit || hasPendingLimitRequest) return;
                        setRequestingLimit(true);
                        const result = await LimitService.requestLimit();
                        setRequestingLimit(false);
                        // Refresh dashboard data after successful request
                        if (result.success) {
                          window.location.reload();
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', py: 3, px: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box sx={{ 
                          mb: 2, 
                          color: hasPendingLimitRequest ? 'text.disabled' : 'info.main',
                          '& svg': { fontSize: 48 },
                          transition: 'transform 0.3s ease'
                        }} className="action-icon">
                          {requestingLimit ? <CircularProgress size={48} /> : <AccountBalance />}
                        </Box>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: hasPendingLimitRequest ? 'text.disabled' : 'text.primary' }}>
                          Limit Request from Bank
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color={hasPendingLimitRequest ? 'text.disabled' : 'text.secondary'} 
                          sx={{ lineHeight: 1.4, flexGrow: 1 }}
                        >
                          {hasPendingLimitRequest 
                            ? 'Your limit request has been submitted and is pending approval' 
                            : 'Request a new credit limit from the bank'}
                        </Typography>
                        {hasPendingLimitRequest && (
                          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Chip 
                              label="Request Submitted" 
                              color="info" 
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Recent Activity */}
        <Card elevation={16} sx={{ 
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          borderTop: '2px solid',
          borderTopColor: 'divider'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Timeline sx={{ mr: 1.5, color: '#1976d2', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Recent Activity
                </Typography>
              </Box>
              <IconButton 
                size="small" 
                sx={{ 
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)'
                  }
                }}
              >
                <ArrowForward />
              </IconButton>
            </Box>
            
            <List sx={{ p: 0 }}>
              {recentActivities.map((activity, index) => (
                <ListItem 
                  key={activity.id} 
                  sx={{ 
                    px: 0,
                    py: 2,
                    my:2,
                    borderBottom:'1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.2s ease',
                    borderRadius: 3,
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    px: 1
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ 
                      backgroundColor: getTypeColor(activity.type),
                      width: 40, 
                      height: 40,
                      '& svg': { fontSize: 20 }
                    }}>
                      {getIconComponent(activity.icon)}
                    </Avatar>
                  </ListItemAvatar>
                  <Box sx={{ flex: 1, ml: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4, color: 'text.primary', mb: 0.5 }}>
                      {activity.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, mb: 0.5 }}>
                      {activity.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {activity.time}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Dashboard;